import { FC } from "react";
import { useIntl } from "react-intl";

import { Box, Typography } from "@mui/material";

import { useUser } from "@framework/provider-user";
import type { IUser } from "@framework/types";

import { formatTrendTooltipDate } from "../../../../../../utils";
import { formatMeasurementLabel } from "../../../../../../utils/measurement-label";
import { formatMeasurementNumber } from "../../../scheme/utils/measurement-format";
import { NYQUIST_TOOLTIP_COLORS } from "../nyquist/tooltip-utils";
import type { IOrbitPairMark } from "./pair-marks";

export const OrbitDataMarkTooltip: FC<{ mark: IOrbitPairMark }> = ({ mark }) => {
  const { formatMessage } = useIntl();
  const { profile } = useUser<IUser>();
  const dataFormat = profile.interfaceSettings.dataFormat;
  const { orbitPair } = mark;
  const formatValue = (value: number, units: number) =>
    formatMeasurementNumber(value, units, dataFormat, { fallbackDigits: 2 });
  const values = `${formatValue(orbitPair.x.value, orbitPair.x.units)} / ${formatValue(
    orbitPair.y.value,
    orbitPair.y.units,
  )}`;
  const control = orbitPair.controlParameter;
  const lines = [
    `${formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.point" })}: ${orbitPair.pointTitle}`,
    `${formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.measure" })}: ${formatMeasurementLabel(
      orbitPair.measureName,
      mark.measureDescription,
    )}`,
    `${formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.dateTime" })}: ${formatTrendTooltipDate(mark.date)}`,
    `${formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.value" })}: ${values}`,
  ];

  return (
    <Box
      sx={{
        p: 1,
        maxWidth: 360,
        bgcolor: NYQUIST_TOOLTIP_COLORS.background,
        color: NYQUIST_TOOLTIP_COLORS.body,
        borderRadius: 1,
        overflowWrap: "anywhere",
        ".MuiTooltip-tooltip &": { m: "-4px -8px" },
      }}
    >
      {lines.map((line, index) => (
        <Typography
          key={index}
          sx={{
            fontSize: 12,
            fontWeight: index === 0 ? 600 : 400,
            color: index === 0 ? NYQUIST_TOOLTIP_COLORS.title : "inherit",
          }}
        >
          {line}
        </Typography>
      ))}
      {control ? (
        <Typography sx={{ fontSize: 12, color: "inherit" }}>
          {control.title}: {formatMeasurementNumber(control.value, null, dataFormat, { fallbackDigits: 2 })}{" "}
          {control.units}
        </Typography>
      ) : null}
    </Box>
  );
};
